import { useEffect, useState } from "react";
import axios, { AxiosError } from "axios";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import { getDataGridFakeData } from "../../../helpers/coappDataGridUtils";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useDatagridSorting from "../../../hooks/useDatagridSorting";
import useToastAlert from "../../../hooks/useToastAlert";
import CoAppDatagridHeader from "../../global/components/datagrid/CoAppDatagridHeader";
import CoAppStandardDataGrid from "../../global/components/datagrid/CoAppStandardDataGrid";
import CoAppKillswitchModal from "../../global/components/modals/CoAppKillswitchModal";

import { getWorkstationManagementColumns, workstationColumns } from "./columns/workstation-management-columns";
import WorkstationDataGridSelectionRow from "./WorkstationDataGridSelectionRow";

export default function WorkstationManagement() {
	const dummyRows = getDataGridFakeData(workstationColumns);
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("workstations");
	const [workstationsSelected, setWorkstationsSelected] = useState([]);
	const [workstationKillswitchDialogOpen, setWorkstationKillswitchDialogOpen] = useState(false);
	const [workstations, setWorkstations] = useState(dummyRows);
	const [workstationsForDisplayInModal, setWorkstationsForDisplayInModal] = useState([]);
	const [activateIsDisabled, setActivateIsDisabled] = useState(false);
	const [deactivateIsDisabled, setDeactivateIsDisabled] = useState(false);
	const [actionType, setActionType] = useState("Activate");
	const [loading, setLoading] = useState(true);
	const [workstationIdFilterSet, setWorkstationIdFilterSet] = useState(false);

	const handleKillSwitchModalToggle = (workstation = null, isDeactivate = false) => {
		if (workstation) {
			setWorkstationsForDisplayInModal([workstation]);
			setWorkstationKillswitchDialogOpen(true);
			setActionType(isDeactivate ? "Deactivate" : "Activate");
		} else {
			let workstationsForDisplay = [];
			workstationsSelected.forEach((selectedUUID) => {
				let workstation = workstations.filter(workstation => {
					if (isDeactivate) {
						return selectedUUID === workstation.uuid && workstation.killSwitchActive;
					} else {
						return selectedUUID === workstation.uuid && !workstation.killSwitchActive;
					}
				});
				if (workstation.length > 0) {
					workstationsForDisplay.push(workstation[0]);
				}
			});
			setWorkstationsForDisplayInModal(workstationsForDisplay);
			setWorkstationKillswitchDialogOpen(true);
			setActionType(isDeactivate ? "Deactivate" : "Activate");
		}
	};

	const columns = getWorkstationManagementColumns(
		handleKillSwitchModalToggle,
		loading
	);

	const initWorkstationManagement = () => {
		axios.get(apiRoutes.getWorkstations, { headers: authHeader })
			.then((res) => {
				setWorkstations(res.data);
				setLoading(false);
				// if there is a workstationId in the url, apply filter to datagrid
				const urlParams = new URLSearchParams(window.location.search);
				const workstationId = urlParams.get("workstationId");
				if (workstationId) {
					let filteredWorkstations = res.data.filter(workstation => workstation.uuid === workstationId);
					setWorkstations(filteredWorkstations);
					setWorkstationIdFilterSet(true);
				}
			}).catch((err) => {
				console.log(err);
			});
	};

	const handleBulkWorkstationStatusToggle = () => {
		let workstationJSON = {
			newStatus: actionType === "Deactivate" ? false : true,
			workstationUUIDs: workstationsForDisplayInModal.map(workstation => workstation.uuid),
		};

		axios.put(apiRoutes.bulkWorkstationKillSwitchToggle, workstationJSON, {
			headers: authHeader
		}).then(() => {
			initWorkstationManagement();
			setWorkstationKillswitchDialogOpen(false);
			setWorkstationsSelected([]);
		})
			.catch((error) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.WORKSTATION_STATUS_TOGGLE_ERROR_MSG);
			});
	};

	const handleWorkstationFilterClear = () => {
		setWorkstationIdFilterSet(false);
		initWorkstationManagement();
		try {
			const urlParams = new URLSearchParams(window.location.search);
			urlParams.delete("workstationId");
			window.history.replaceState({}, "", window.location.pathname + "?" + urlParams.toString());
		} catch (error) {
			console.error(error);
		}
	};

	const handleWorkstationStatusToggle = () => {
		axios.put(apiRoutes.workstationKillSwitch(workstationsForDisplayInModal[0].uuid), { newStatus: actionType === "Deactivate" ? false : true }, { headers: authHeader })
			.then(() => {
				initWorkstationManagement();
				setWorkstationKillswitchDialogOpen(false);
			})
			.catch((error) => {
				console.error(error);
				handleToastAlert(messageLevels.ERROR, messages.WORKSTATION_STATUS_TOGGLE_ERROR_MSG);
			});
	};

	const handleSelectionModelChange = (newSelectionModel) => {
		const fullWorkstations = workstations.filter(workstation => newSelectionModel.includes(workstation.uuid));
		const hasActiveKillSwitches = fullWorkstations.some(workstation => workstation.killSwitchActive);
		const hasInactiveKillSwitches = fullWorkstations.some(workstation => !workstation.killSwitchActive);

		if (!hasActiveKillSwitches) {
			setDeactivateIsDisabled(true);
		} else {
			setDeactivateIsDisabled(false);
		}
		if (!hasInactiveKillSwitches) {
			setActivateIsDisabled(true);
		} else {
			setActivateIsDisabled(false);
		}
		setWorkstationsSelected(newSelectionModel);
	};

	/**
     * Update the row in the database, only pass the fields that are updatable
     * @param newRow - The new row data
     * @returns The row data for the grid to update the row
     * Note: If the dataFieldType is not an object aka the user has not selected a data type but tries to save,
     * we return the old row to prevent the grid from attempting to save the row
     */
	const handleRowUpdate = async (newRow, oldRow) => {
		if (typeof newRow.comment !== "string") return oldRow;
		const dataToUpdate = {
			comment: newRow.comment,
		};

		try {
			const res = await axios.patch(apiRoutes.patchWorkstation(newRow.uuid), { data: dataToUpdate }, { headers: authHeader });
			if (res.status !== 200) {
				throw new Error(messages.FIELD_UPDATE_FAILED_ERROR_MSG);
			}
		} catch (err) {
			handleToastAlert(messageLevels.ERROR, messages.FIELD_UPDATE_FAILED_ERROR_MSG);
		}
		return newRow;
	};

	useEffect(() => {
		initWorkstationManagement();
		handleInitializeSortModel();
	}, []);

	return (
		<>
			<CoAppDatagridHeader
				title="Workstations"
				addIsPresent={false}
				editIsPresent={false}
				deleteIsPresent={false}
			/>
			<CoAppStandardDataGrid
				columns={columns}
				rows={workstations}
				allowSelection={true}
				targetResource="workstations"
				selectionModel={workstationsSelected}
				setSelectionModel={handleSelectionModelChange}
				handleModalToggle={() => handleKillSwitchModalToggle()}
				customSelectionChangeHandler={handleSelectionModelChange}
				customSelectionRow={
					<WorkstationDataGridSelectionRow
						shouldShow={workstationsSelected.length > 0}
						selectedItems={workstationsSelected}
						workstations={workstations}
						onClearClickHandler={() => setWorkstationsSelected([])}
						bulkActivateHandler={() => handleKillSwitchModalToggle(null, false)}
						bulkDeactivateHandler={() => handleKillSwitchModalToggle(null, true)}
						activateIsDisabled={activateIsDisabled}
						deactivateIsDisabled={deactivateIsDisabled}
					/>
				}
				processRowUpdate={handleRowUpdate}
				onProcessRowUpdateError={(err) => handleToastAlert(messageLevels.ERROR, err.message)}
				loading={loading}
				sortModel={sortModel}
				handleSortModelChange={handleSortModelChange}
				idFilterSet={workstationIdFilterSet}
				clearFilterHandler={handleWorkstationFilterClear}
			/>
			<CoAppKillswitchModal
				dialogOpen={workstationKillswitchDialogOpen}
				dialogTitle={workstationsSelected.length > 1 ? `${actionType} kill switch on selected workstations?` : `${actionType} workstation kill switch?`}
				dialogMessage={messages.WORKSTATION_KILLSWITCH_MSG(workstationsSelected.length, workstationsForDisplayInModal[0]?.killSwitchActive)}
				confirmClickHandler={
					workstationsForDisplayInModal.length > 1 ?
						() => handleBulkWorkstationStatusToggle()
						:
						() => handleWorkstationStatusToggle()
				}
				cancelClickHandler={() => setWorkstationKillswitchDialogOpen(false)}
				actionText={`${actionType} Kill Switch`}
				workstations={workstationsForDisplayInModal}
				isOrganizationWide={false}
			/>
		</>
	);
}